import React from "react";

import { faPen } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faPlus } from "@performyard/fontawesome/pro-solid-svg-icons";
import { Alert, ButtonStandard, Text } from "@performyard/py-components";

import { GeneratedContentContainer } from "./GeneratedContentContainer";
import { GoalPreviewCard } from "./GoalPreviewCard";

import type { GoalSuggestionState } from "../types";

type GoalSuggestionsContainerProps = {
	suggestionStates: GoalSuggestionState[];
	onCreateGoal: (suggestionId: string) => void;
	onCreateAllGoals: () => void;
	onEditGoals?: () => void;
	creatingSuggestionIds?: Set<string>;
	isCreatingAll?: boolean;
};

export const GoalSuggestionsContainer: React.FC<GoalSuggestionsContainerProps> = ({
	suggestionStates,
	onCreateGoal,
	onCreateAllGoals,
	onEditGoals,
	creatingSuggestionIds,
	isCreatingAll,
}) => {
	const visibleSuggestions = suggestionStates.filter((gs) => gs.status !== "deleted");
	const allDeleted = visibleSuggestions.length === 0;
	// Goal creation isn't tethered to any page, so a restored card that was never created
	// counts as pending exactly like a live one — the bulk button and the completion line
	// follow the cards' real statuses, which survive a reload (Figma 1373:13686).
	const pending = visibleSuggestions.filter((gs) => gs.status === "pending");
	const hasPending = pending.length > 0;
	// Mirrors the bulk button's visibility so the group reports completion only where it offered
	// a bulk action — a lone card already says "Goal Created" on its own chip.
	const created = visibleSuggestions.filter((gs) => gs.status === "created");
	const allCreated = visibleSuggestions.length > 1 && !hasPending;

	return (
		<GeneratedContentContainer
			headerAction={
				onEditGoals && hasPending ? { label: "Edit Goals", iconLeft: faPen, onClick: onEditGoals } : undefined
			}
		>
			{allDeleted ? (
				<Text size="paragraph-3" color="neutral-7">
					All suggested goals have been removed.
				</Text>
			) : (
				<div className="flex flex-col gap-2">
					{visibleSuggestions.map((gs) => (
						<GoalPreviewCard
							key={gs.suggestion.id}
							suggestionState={gs}
							onCreateGoal={() => onCreateGoal(gs.suggestion.id)}
							isCreating={creatingSuggestionIds?.has(gs.suggestion.id)}
						/>
					))}
					{pending.length > 1 && (
						<ButtonStandard
							variant="solid"
							color="primary"
							size="medium"
							iconLeft={faPlus}
							onClick={onCreateAllGoals}
							disabled={isCreatingAll}
						>
							{isCreatingAll ? "Creating..." : "Create All Goals"}
						</ButtonStandard>
					)}
					{allCreated && (
						<Alert
							type="success"
							size="paragraph-3"
							items="center"
							message={`${created.length} ${created.length === 1 ? "Goal" : "Goals"} Created`}
						/>
					)}
				</div>
			)}
		</GeneratedContentContainer>
	);
};

export default GoalSuggestionsContainer;
